'use client';

import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Slider, 
  TextField, 
  Button,
  Chip,
  Divider,
  Paper,
  Stack
} from '@mui/material';
import TrendingUp from '@/letsgo/icons/TrendingUp';
import Calculate from '@/letsgo/icons/Calculate';
import CheckCircle from '@/letsgo/icons/CheckCircle';
import ArrowForward from '@/letsgo/icons/ArrowForward';
import { marketingPrimaryCtaSx } from '@/lib/marketingButtonStyles';

interface ROIData {
  platforms: number;
  postsPerWeek: number;
  hoursPerWeek: number;
  hourlyRate: number;
}

const DEFAULT_VALUES: ROIData = {
  platforms: 5,
  postsPerWeek: 20,
  hoursPerWeek: 10,
  hourlyRate: 50
};

const PLATFORM_COSTS = {
  individual: 15, // Average cost per platform tool
  competitorPro: 29 // Typical competitor Pro monthly cost for comparison
};

const CREATOR_BENEFITS = [
  'Unified Dashboard',
  'AI-Powered Content',
  'Advanced Analytics',
  'Team Collaboration',
  'White-Label Options',
  '24/7 Support'
] as const;

function computeSavings(v: ROIData) {
  const individualCost = v.platforms * PLATFORM_COSTS.individual;
  const competitorCost = PLATFORM_COSTS.competitorPro;
  const monthlySavings = individualCost - competitorCost;
  const yearlySavings = monthlySavings * 12;
  const timeSaved = v.hoursPerWeek * 0.5;
  const efficiency = 50;
  return {
    monthly: monthlySavings,
    yearly: yearlySavings,
    timeSaved,
    efficiency
  };
}

export function ROICalculator() {
  const [values, setValues] = useState<ROIData>(DEFAULT_VALUES);
  const savings = useMemo(() => computeSavings(values), [values]);

  const handleSliderChange = (field: keyof ROIData) => (event: Event, newValue: number | number[]) => {
    setValues(prev => ({
      ...prev,
      [field]: newValue as number
    }));
  };

  const handleInputChange = (field: keyof ROIData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || 0;
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box
      data-roi-layout="v2"
      data-floai-marketing-roi="2025-03-grid-stack"
      sx={{
        py: 8,
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        bgcolor: 'background.default',
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Stack
          spacing={1}
          alignItems="center"
          sx={{
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            textAlign: 'center',
            mb: { xs: 4, md: 6 },
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
              textAlign: 'center',
              width: '100%',
            }}
          >
            Calculate Your ROI
          </Typography>
          <Typography
            variant="h6"
            component="p"
            color="text.secondary"
            sx={{ textAlign: 'center', width: '100%', px: { xs: 1, sm: 0 }, m: 0 }}
          >
            See how much you could save with FloAI Studio
          </Typography>
        </Stack>

        <Box
          component="section"
          aria-label="ROI calculator inputs and results"
          sx={{ width: '100%', minWidth: 0, display: 'block' }}
        >
        <Box
          sx={{
            display: 'grid',
            width: '100%',
            minWidth: 0,
            gap: { xs: 3, md: 4 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            alignItems: 'stretch',
          }}
        >
            <Card
              sx={{
                minWidth: 0,
                width: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                height: { xs: 'auto', sm: '100%' },
              }}
            >
              <CardContent
                sx={{
                  pt: 4,
                  px: 4,
                  pb: { xs: 4, sm: 5 },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { sm: 0 },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calculate sx={{ color: 'primary.main' }} />
                  Your Current Setup
                </Typography>

                {/* Platforms */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Number of Platforms: {values.platforms}
                  </Typography>
                  <Slider
                    value={values.platforms}
                    onChange={handleSliderChange('platforms')}
                    min={1}
                    max={16}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Instagram, YouTube, TikTok, LinkedIn, etc.
                  </Typography>
                </Box>

                {/* Posts per Week */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Posts per Week: {values.postsPerWeek}
                  </Typography>
                  <Slider
                    value={values.postsPerWeek}
                    onChange={handleSliderChange('postsPerWeek')}
                    min={1}
                    max={50}
                    step={1}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 10, label: '10' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' }
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ mb: 2 }}
                  />
                </Box>

                {/* Hours per Week */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Hours Spent per Week: {values.hoursPerWeek}
                  </Typography>
                  <Slider
                    value={values.hoursPerWeek}
                    onChange={handleSliderChange('hoursPerWeek')}
                    min={1}
                    max={40}
                    step={1}
                    marks={[
                      { value: 1, label: '1h' },
                      { value: 10, label: '10h' },
                      { value: 20, label: '20h' },
                      { value: 40, label: '40h' }
                    ]}
                    valueLabelDisplay="auto"
                    sx={{ mb: 2 }}
                  />
                </Box>

                {/* Hourly Rate */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Your Hourly Rate: ${values.hourlyRate}
                  </Typography>
                  <TextField
                    type="number"
                    value={values.hourlyRate}
                    onChange={handleInputChange('hourlyRate')}
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                    }}
                    sx={{ mb: 2 }}
                  />
                </Box>
              </CardContent>
            </Card>

              <Card
                sx={{
                  minWidth: 0,
                  width: '100%',
                  minHeight: 0,
                  bgcolor: 'primary.50',
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  height: { xs: 'auto', sm: '100%' },
                }}
              >
                <CardContent
                  sx={{
                    pt: 4,
                    px: 4,
                    // Breathing room below CTA; pairs with row stretch so both cards match the taller column.
                    pb: { xs: 5, sm: 11 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { sm: 0 },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp sx={{ color: 'success.main' }} />
                    Your Savings with FloAI
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      width: '100%',
                      gap: 2,
                      minWidth: 0,
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    }}
                  >
                      <Paper sx={{ p: 2.5, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText', minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          ${savings.monthly.toFixed(0)}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          Monthly savings
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2.5, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText', minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          ${savings.yearly.toFixed(0)}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          Yearly savings
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText', minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          {savings.timeSaved.toFixed(1)}h
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                          Time saved / week
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText', minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          {savings.efficiency}%
                        </Typography>
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                          Efficiency gain
                        </Typography>
                      </Paper>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Cost Comparison */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Cost Comparison
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ flex: '1 1 200px', minWidth: 0 }}>
                        Individual tools ({values.platforms} platforms):
                      </Typography>
                      <Typography sx={{ fontWeight: 'bold', flexShrink: 0 }}>
                        ${(values.platforms * PLATFORM_COSTS.individual).toFixed(0)}/mo
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ flex: '1 1 200px', minWidth: 0 }}>
                        Competitor Pro (reference):
                      </Typography>
                      <Typography sx={{ fontWeight: 'bold', color: 'primary.main', flexShrink: 0 }}>
                        ${PLATFORM_COSTS.competitorPro}/mo
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        You save
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        ${savings.monthly.toFixed(0)}/mo
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flexShrink: 0, mt: '12px' }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      endIcon={<ArrowForward />}
                      sx={marketingPrimaryCtaSx({
                        py: 1.25,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-2px)', boxShadow: 4 },
                      })}
                    >
                      Start Your Free Trial
                    </Button>
                  </Box>
                </CardContent>
              </Card>
        </Box>
        </Box>

        <Box
          component="section"
          aria-labelledby="roi-creators-heading"
          sx={{
            width: '100%',
            minWidth: 0,
            display: 'block',
            flexShrink: 0,
            mt: { xs: 6, md: 8 },
            pt: { xs: 5, md: 6 },
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Stack spacing={3} sx={{ width: '100%', alignItems: 'stretch' }}>
            <Typography id="roi-creators-heading" variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', width: '100%' }}>
              Why Creators Choose FloAI
            </Typography>
            <Box
              sx={{
                display: 'grid',
                width: '100%',
                gap: 2,
                minWidth: 0,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gridTemplateRows: { xs: 'none', md: 'repeat(3, auto)' },
                gridAutoFlow: { xs: 'row', md: 'column' },
              }}
            >
              {CREATOR_BENEFITS.map((benefit) => (
                <Chip
                  key={benefit}
                  icon={<CheckCircle />}
                  label={benefit}
                  color="success"
                  variant="outlined"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    minHeight: 40,
                    minWidth: 0,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    justifyContent: 'flex-start',
                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.35 },
                  }}
                />
              ))}
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
